import React, { useState, useRef, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import AiChat from "@/components/AiChat";
import CommercialCopilot from "@/components/CommercialCopilot";
import { CopilotPanel } from "@/components/CopilotPanel";
import LanguageToggle from "@/components/LanguageToggle";
import ThemeToggle from "@/components/ThemeToggle";
import { GlobalZonaSelector } from "@/components/GlobalZonaSelector";
import CommandPalette from "@/components/CommandPalette";
import { getVisibleGroups, ADMIN_ITEMS, DASHBOARD_ITEM, MI_TRABAJO_ITEM, COCKPIT_ITEM } from "@/lib/navigation";
import { pushRecent } from "@/lib/recents";
import {
  LogOut, Bell, Menu, X, ChevronRight, ChevronDown, Check, Search, Star,
} from "lucide-react";

const NOTIF_TYPE_STYLES = {
  offer_approved: { dot: "bg-green-500", label: "Oferta aprobada" },
  offer_rejected: { dot: "bg-red-500", label: "Oferta rechazada" },
  offer: { dot: "bg-blue-500", label: "Oferta" },
  workitem: { dot: "bg-purple-500", label: "Work item" },
  sprint_delayed: { dot: "bg-orange-500", label: "Sprint retrasado" },
  hot_opportunity: { dot: "bg-amber-500", label: "Oportunidad Premium" },
  tender360_premium: { dot: "bg-amber-500", label: "Premium Tender360" },
  info: { dot: "bg-gray-400", label: "Info" },
};

// Iter282 · rutas de la parte comercial donde se muestra el Copiloto IA
const COMMERCIAL_COPILOT_ROUTES = [
  "/licitaciones", "/ofertas-comercial", "/mercado-sectorial",
  "/tender-intelligence", "/ytd-board", "/tender360", "/hit-rate-zonas",
  "/business-review",
];

// Rutas ejecutivas: aquí el Copiloto Ejecutivo (Jev, determinista) sustituye al
// AiChat genérico de IT (irrelevante en la portada/zonas de dirección).
const EXECUTIVE_COPILOT_ROUTES = [
  "/cockpit", "/resultados-por-zona", "/zonas-admin",
];

const ROLE_LABELS = {
  admin: "Administrador",
  it_validator: "Validador TIC",
  cfo: "Director Financiero",
  offer_manager: "Responsable Oferta",
  checklist_manager: "Gestor Checklists",
  delegado_regional: "Delegado Regional",
};

function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleNotifClick = (n) => {
    markRead(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={17} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5" data-testid="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden animate-fade-up" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Notificaciones</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#007934] hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
                <Check size={11} /> Marcar todas leídas
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400">
                <Bell size={24} className="mx-auto mb-2 opacity-30" />
                <p className="text-xs">Sin notificaciones</p>
              </div>
            ) : (
              notifications.slice(0, 20).map(n => {
                const cfg = NOTIF_TYPE_STYLES[n.type] || NOTIF_TYPE_STYLES.info;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleNotifClick(n)}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${!n.read ? "bg-blue-50/40" : ""}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read ? cfg.dot : "bg-gray-200"}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-snug ${!n.read ? "font-medium text-gray-900" : "text-gray-600"}`}>{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {new Date(n.created_at).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sidebar nav item ─────────────────────────────────────────────────────────
function NavItem({ to, icon: Icon, label, collapsed, t, isFav, onToggleFav }) {
  const text = label.startsWith("layout.") ? t(label) : label;
  return (
    <NavLink
      to={to}
      title={collapsed ? text : undefined}
      aria-label={text}
      className={({ isActive }) =>
        `group/item relative flex items-center gap-2.5 rounded-lg mb-px transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 ${
          collapsed ? "justify-center py-2.5 px-0" : "pl-3 pr-1.5 py-[7px]"
        } ${isActive
          ? "bg-white/[0.14] text-white font-medium"
          : "text-emerald-100/70 hover:bg-white/[0.07] hover:text-white"}`
      }
      data-testid={`nav-${label.replace(/^layout\./, "").toLowerCase().replace(/\s/g, "-")}`}
    >
      {({ isActive }) => (
        <>
          {isActive && !collapsed && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[3px] rounded-r-full bg-emerald-300" />
          )}
          <Icon size={16} className="flex-shrink-0" strokeWidth={isActive ? 2.2 : 1.8} aria-hidden="true" />
          {!collapsed && <span className="text-[13px] truncate flex-1">{text}</span>}
          {!collapsed && onToggleFav && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(to); }}
              className={`p-1 rounded transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 ${
                isFav ? "opacity-90 text-amber-300" : "opacity-0 group-hover/item:opacity-50 text-emerald-100 hover:!opacity-100 focus-visible:opacity-100"
              }`}
              title={isFav ? "Quitar de favoritos" : "Añadir a favoritos"}
              aria-label={isFav ? `Quitar ${text} de favoritos` : `Añadir ${text} a favoritos`}
              data-testid={`fav-toggle-${to.replace(/\//g, "")}`}
            >
              <Star size={11} fill={isFav ? "currentColor" : "none"} aria-hidden="true" />
            </button>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  const isCommercialCopilot = COMMERCIAL_COPILOT_ROUTES.some((r) => location.pathname.startsWith(r));
  const isExecutiveCopilot = EXECUTIVE_COPILOT_ROUTES.some((r) => location.pathname.startsWith(r));

  // ── Favoritos por usuario ──
  const favKey = `valoriza-favs-${user?.email || "anon"}`;
  const [favs, setFavs] = useState([]);
  useEffect(() => {
    try { setFavs(JSON.parse(localStorage.getItem(favKey)) || []); } catch { setFavs([]); }
  }, [favKey]);
  const toggleFav = (to) => {
    setFavs((prev) => {
      const next = prev.includes(to) ? prev.filter((x) => x !== to) : [...prev, to];
      try { localStorage.setItem(favKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // ── Estado de grupos colapsables (persistido) ──
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("valoriza-nav-open")) || {}; } catch { return {}; }
  });
  const toggleGroup = (id, current) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [id]: !current };
      try { localStorage.setItem("valoriza-nav-open", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  const groups = getVisibleGroups(user);
  const allItems = [DASHBOARD_ITEM, COCKPIT_ITEM, MI_TRABAJO_ITEM, ...groups.flatMap((g) => g.items)];
  const favItems = favs.map((to) => allItems.find((i) => i.to === to)).filter(Boolean);
  const containsActive = (g) => g.items.some((i) => location.pathname.startsWith(i.to));

  // Registrar visitas a módulos para "Recientes" del Command Palette
  useEffect(() => {
    if (!user) return;
    const item = [...allItems, ...ADMIN_ITEMS].find((i) => i.to === location.pathname);
    if (item && item.to !== "/dashboard") {
      pushRecent(user.email, { path: item.to, title: t(item.label), type: "nav" });
    }
  }, [location.pathname]); // eslint-disable-line

  const SidebarContent = () => (
    <div className="flex flex-col h-full sidebar">
      {/* Logo */}
      <div className={`px-4 pt-5 pb-4 border-b border-white/[0.07] ${collapsed ? "px-2" : ""}`}>
        <div className="flex items-center gap-2 min-h-[40px]">
          {!collapsed ? (
            <div className="flex flex-col">
              <img src="/valoriza-logo.svg" alt="Valoriza" className="h-8 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
              <p className="text-emerald-200/70 text-[9.5px] leading-tight mt-1.5 pl-0.5 tracking-[0.18em] uppercase font-medium">Plataformas Corporativas</p>
            </div>
          ) : (
            <div className="w-8 h-8 mx-auto bg-white/15 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs">V</span>
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-2 overflow-y-auto" aria-label="Navegación principal">
        {/* Dashboard — siempre visible */}
        {user?.role !== "checklist_manager" && <NavItem {...DASHBOARD_ITEM} collapsed={collapsed} t={t} />}
        {user?.role !== "checklist_manager" && <NavItem {...COCKPIT_ITEM} collapsed={collapsed} t={t} />}
        {user?.role !== "checklist_manager" && <NavItem {...MI_TRABAJO_ITEM} collapsed={collapsed} t={t} />}

        {/* Favoritos */}
        {!collapsed && favItems.length > 0 && (
          <div className="mt-2" data-testid="sidebar-favorites">
            <p className="px-3 pt-2 pb-1 text-[9px] font-bold text-amber-200/50 uppercase tracking-[0.16em] flex items-center gap-1">
              <Star size={8} fill="currentColor" /> {t("layout.sectionFavorites")}
            </p>
            {favItems.map((item) => (
              <NavItem key={`fav-${item.to}`} {...item} collapsed={collapsed} t={t} isFav onToggleFav={toggleFav} />
            ))}
          </div>
        )}

        {/* Grupos colapsables */}
        {collapsed ? (
          // Modo icono: lista plana
          <div className="mt-1 space-y-px">
            {groups.flatMap((g) => g.items).map((item) => (
              <NavItem key={item.to} {...item} collapsed t={t} />
            ))}
          </div>
        ) : (
          groups.map((group) => {
            const active = containsActive(group);
            const isOpen = openGroups[group.id] !== undefined ? openGroups[group.id] : active;
            return (
              <div key={group.id} className="mt-1.5">
                <button
                  onClick={() => toggleGroup(group.id, isOpen)}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.16em] text-emerald-200/45 hover:text-emerald-100/80 transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
                  data-testid={`nav-group-${group.id}`}
                  aria-expanded={isOpen}
                  aria-controls={`nav-group-panel-${group.id}`}
                >
                  <span className="flex items-center gap-1.5">
                    {group.label.startsWith("layout.") ? t(group.label) : group.label}
                    {!isOpen && active && <span className="w-1 h-1 rounded-full bg-emerald-300 animate-pulse" aria-label="Sección con elemento activo" />}
                  </span>
                  <ChevronDown size={11} className={`transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} aria-hidden="true" />
                </button>
                <div id={`nav-group-panel-${group.id}`} className={`overflow-hidden transition-all duration-200 ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                  {group.items.map((item) => (
                    <NavItem key={item.to} {...item} collapsed={false} t={t} isFav={favs.includes(item.to)} onToggleFav={toggleFav} />
                  ))}
                </div>
              </div>
            );
          })
        )}

        {/* Admin — T09 (tanda integral 12/10/2026): antes esta sección no
            tenía rótulo propio, aparecía como una lista suelta tras un
            separador. Ahora se etiqueta "Administración" igual que el
            resto de grupos, coherente con la agrupación de navegación. */}
        {user?.role === "admin" && (
          <div className="mt-3 pt-2 border-t border-white/[0.07]">
            {!collapsed && (
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40" data-testid="nav-admin-section-label">
                {t("layout.sectionAdmin")}
              </p>
            )}
            {ADMIN_ITEMS.map((item) => (
              <NavItem key={item.to} {...item} collapsed={collapsed} t={t} />
            ))}
          </div>
        )}
      </nav>

      {/* User */}
      <div className="px-3 py-3.5 border-t border-white/[0.07]">
        <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : "px-1"}`}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400/40 to-emerald-600/40 ring-1 ring-white/20 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-semibold uppercase">{user?.name?.slice(0, 2) || "??"}</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-semibold truncate">{user?.name}</p>
              <p className="text-emerald-200/70 text-[11px] truncate">{ROLE_LABELS[user?.role] || "Técnico"}</p>
            </div>
          )}
          {!collapsed && (
            <button onClick={handleLogout} className="text-emerald-200/70 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10" data-testid="logout-btn" title="Cerrar sesión">
              <LogOut size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden print:block print:h-auto print:overflow-visible print:bg-white">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col transition-all duration-300 ${collapsed ? "w-16" : "w-[252px]"} flex-shrink-0 relative print:hidden`}
        style={{ background: "linear-gradient(178deg, #022D18 0%, #015A28 115%)" }}
      >
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-8 bg-white dark:bg-card border border-gray-200 rounded-full p-1 shadow-md text-gray-500 hover:text-gray-700 z-10 transition-colors"
          data-testid="sidebar-collapse-btn"
        >
          <ChevronRight size={12} className={`transition-transform ${collapsed ? "" : "rotate-180"}`} />
        </button>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-[270px] flex flex-col" style={{ background: "linear-gradient(178deg, #022D18 0%, #015A28 115%)" }}>
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 text-white z-10">
              <X size={20} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden print:block print:overflow-visible print:h-auto">
        {/* Top bar */}
        <header className="bg-white/85 backdrop-blur-xl border-b border-gray-100 px-4 sm:px-6 py-2.5 flex items-center justify-between flex-shrink-0 print:hidden z-20">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden text-gray-500 hover:text-gray-700">
              <Menu size={20} />
            </button>
            <div className="hidden sm:block">
              <p className="text-xs text-gray-400 tracking-wide">{t("layout.appTagline")}</p>
            </div>
            <GlobalZonaSelector />
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCmdOpen(true)}
              className="hidden sm:flex items-center gap-2 text-sm text-gray-400 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 hover:border-[#007934] hover:text-gray-600 transition-colors w-52 mr-1"
              data-testid="global-search-btn"
            >
              <Search size={13} />
              <span className="text-xs">Buscar...</span>
              <kbd className="ml-auto text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded border border-gray-200">⌘K</kbd>
            </button>
            <ThemeToggle />
            <LanguageToggle />
            <NotificationBell />
            <div className="flex items-center gap-2 bg-[#F0FDF4] text-[#007934] px-3 py-1.5 rounded-full text-xs font-medium ml-1" data-testid="header-user-chip">
              <div className="w-1.5 h-1.5 bg-[#007934] rounded-full animate-pulse" />
              {user?.name?.split(" ")[0] || "Usuario"}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 print:overflow-visible print:h-auto print:p-0">
          <Outlet />
        </main>
      </div>
      <CommandPalette open={cmdOpen} setOpen={setCmdOpen} />
      {/* Iter290 · El AiChat genérico (IT/Proyectos, Perplexity) y el Copiloto
          Comercial (Claude Sonnet 5, grounded en ofertas_comercial) coexistían
          en las mismas páginas comerciales — confusión real reportada: un
          usuario preguntó por hit rate comercial al bot equivocado (derecha)
          y recibió datos de "Ofertas TIC" (módulo IT, nada que ver). En
          páginas comerciales sólo se muestra el Copiloto Comercial.
          Iter393 · En rutas EJECUTIVAS (Cockpit/zonas) se muestra el Copiloto
          Ejecutivo (Jev, determinista) en vez del AiChat de IT. Siempre un
          único bot por página. */}
      {isCommercialCopilot && <CommercialCopilot />}
      {!isCommercialCopilot && isExecutiveCopilot && <CopilotPanel />}
      {!isCommercialCopilot && !isExecutiveCopilot && <AiChat />}
    </div>
  );
}
